import type { ServerLogErrorMessage } from '@/types/errorHandler';
import type { CountryCode } from '~/graphql/generated/graphql';

/**
 * Geo region plumbing for the geo routing prompts.
 *
 * Front Door (prod) — or `EMULATED_GEO` via Caddy (dev) — attaches an
 * `X-Phoenix-Geo: USA | AUS | OTHER` header. SSR seeds it into shared Nuxt state
 * (hydrated to the client), mirroring how the cluster is resolved from
 * `X-Cluster-Code`. On the frontend the value is cosmetic (it drives a
 * dismissable popup); the backend also records it against external-survey
 * launches as a label and relies on Front Door overwriting it — so it is never
 * a client-supplied value there. The clusters we serve live in
 * `utils/clusters.ts`.
 */

const noop: ServerLogErrorMessage = () => {};

/** Detected visitor region. `OTHER` = "not a region we serve yet". */
export const GEO_REGIONS = ['USA', 'AUS', 'OTHER'] as const;

export type GeoRegion = (typeof GEO_REGIONS)[number];

/**
 * The regions a visitor can prefer — only the ones we actually serve, which is
 * deliberately narrower than "a country the API models". `satisfies` pins the
 * values to `CountryCode` so the two can't drift, while leaving this list the
 * authority on what an untrusted cookie may name; the import is type-only, so
 * the generated GraphQL module never reaches the bundle.
 */
export const PREFERRED_REGIONS = ['USA', 'AUS'] as const satisfies readonly Extract<GeoRegion, `${CountryCode}`>[];

export type PreferredRegion = (typeof PREFERRED_REGIONS)[number];

/** Shared-state key for the detected geo region (SSR-seeded, hydrated to client). */
export const PHOENIX_GEO_STATE = 'phoenix-geo';

/** Shared-state key for the crawler flag (SSR-seeded from the User-Agent). */
export const PHOENIX_IS_BOT_STATE = 'phoenix-is-bot';

/**
 * The visitor's chosen region. Deliberately a plain (non-cluster-scoped) cookie:
 * `useClusterCookie` prefixes names per cluster so US/AU tabs never share state,
 * but a region preference exists precisely to be read by *both* consumer
 * clusters on the shared apex — the AU deployment must see a preference stored
 * while browsing the US one, and vice versa.
 */
export const REGION_PREFERENCE_COOKIE = 'geoRegionPreference';

/**
 * Session cookie set when the visitor declines a region prompt ("stay on this
 * page" in the selector or the auto-routing dialog). Suppresses further
 * preference prompts and auto-redirects for the browser session; the stored
 * preference itself is never changed by a decline.
 */
export const GEO_ROUTING_DISMISSED_COOKIE = 'geoRoutingPromptDismissed';

/**
 * One-shot marker dropped by the SSR preference-redirect (which runs where no
 * analytics provider exists) carrying the origin region; the landing page reads
 * it, fires the `region_switched` event with `source: 'redirect'`, and clears
 * it. Apex-wide like the preference so the destination cluster can read it.
 */
export const GEO_REDIRECT_TRACKING_COOKIE = 'geoRegionRedirected';

/** Selector/switcher display flags. Emoji per the spec (`🇺🇸 United States ▼`). */
export const GEO_FLAG_EMOJI: Record<PreferredRegion, string> = {
  USA: '🇺🇸',
  AUS: '🇦🇺',
};

/** Narrow an untrusted cookie value to a region we serve, or `null`. */
export const parseRegionPreference = (value: unknown): PreferredRegion | null =>
  PREFERRED_REGIONS.find(region => region === value) ?? null;

// Deliberately broad: suppressing a cosmetic prompt for a false-positive human
// is harmless, while a full-screen interstitial served to a crawler risks an
// SEO penalty. Self-identifying crawlers only — plain HeadlessChrome is NOT
// treated as a bot (Playwright/e2e must see the real experience). Never use
// for auth/tenancy decisions.
const BOT_USER_AGENT_PATTERN
  = /bot|crawl|spider|slurp|bingpreview|lighthouse|facebookexternalhit|whatsapp|telegram|linkedin|twitter|pinterest/i;

/** Best-effort crawler detection from a User-Agent header. */
export const isBotUserAgent = (userAgent: string | null | undefined): boolean =>
  !!userAgent && BOT_USER_AGENT_PATTERN.test(userAgent);

/**
 * Resolve the `X-Phoenix-Geo` header to a `GeoRegion`, or `null` when absent or
 * unexpected. An unexpected value is a broken Front Door contract — the caller
 * passes a Datadog logger (`logErrorMessage`); defaults to a no-op for callers
 * (and tests) without one.
 */
export const resolveGeo = (
  header: string | null | undefined,
  logErrorMessage: ServerLogErrorMessage = noop,
): GeoRegion | null => {
  if (!header) {
    return null;
  }

  const normalised = header.trim().toUpperCase();
  const match = GEO_REGIONS.find(region => region === normalised);
  if (!match) {
    logErrorMessage('[resolveGeo] unexpected X-Phoenix-Geo value', { value: header });
    return null;
  }

  return match;
};
